import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Progress } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { autoLoginShopifyWithOneTimeToken } from "../../../../user/services/store/user.actions";
import { useLocation, useNavigate } from "react-router";
import { ModalInfo } from "../../../../_UI/Components/ModalInfo";
import ShopifyAppstoreNewUserRegistration from "../components/ShopifyAppstoreNewUserRegistration";
import ShopifyAppstoreExistingUserLogin from "../components/ShopifyAppstoreExistingUserLogin";
import { PathwayFormGrid } from "../../../../_UI/StyledComponents/PathwayForm";
import PathwayTextInputGroup from "../../../../_UI/Components/Inputs/PathwayTextInputGroup";
import isEmail from "validator/es/lib/isEmail";
import { login, logout, setUpResetPassword } from "../../../../user/services/store/authentication.actions";
import { appstoreSetupReducerActions } from "../../../../_services/store";
import {
    connectNewShopToExistingOrgAndUser,
    connectNewShopToExistingUserInitialLogin,
    connectNewShopToExistingUserInitialLoginViaSSO,
    resolveConnectNewShopToExistingUserSSOMfa,
    cancelConnectNewShopToExistingUserSSOMfa,
    logExistingUserIntoExistingStore,
    logExistingUserIntoExistingStoreViaSSO,
    resolveLogExistingUserIntoExistingStoreMfa,
    createNewOrgForExistingUser,
    submitNewOrganizationAndUserRegistrationIncludingExistingAuthData,
    submitNewUserToExistingShopifyOrg,
} from "../../../../user/services/store/appstoreSetup.actions";
import LoadingSpinner from "../../../../_UI/Components/LoadingSpinner";
import { LoadingSpinnerButton, PathwayLinkButton } from "../../../../_UI/Components/PathwayButtons";
import AlertComponent from "../../../../_UI/Components/Alerts/AlertComponent";
import useRegistrationLoadingBar from "../../../../_hooks/useRegistrationLoadingBar";
import usePhoneField from "../../../../_hooks/usePhoneField";
import useAnalytics from "../../../../_hooks/useAnalytics";
import { submitAnalytics } from "../../../../organization/myOrganization/services/store/myOrganization.actions";

const NEW_USER = "NEW_USER";
const EXISTING_USER = "EXISTING_USER";
const TABS = {
    NEW_USER,
    EXISTING_USER,
};
const TAB_LIST = () => {
    return [
        {
            description: "Registrieren",
            identifier: TABS.NEW_USER,
        },
        {
            description: "Login",
            identifier: TABS.EXISTING_USER,
        },
    ];
};

const ShopifyRegistrationScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const queryParameters = useMemo(() => {
        return new URLSearchParams(location.search);
    }, [location.search]);

    const appstoreSetup = useSelector((state) => state.appstoreSetup);
    const authentication = useSelector((state) => state.authentication);
    const [registrationPrompt, setRegistrationPrompt] = useState("");
    const [resetMail, setResetMail] = useState("");
    const [resetSuccessMessage, setResetSuccessMessage] = useState(null);
    const [forgetPasswordModal, setForgetPasswordModal] = useState(false);
    const [loginSubmitted, setLoginSubmitted] = useState(false);
    const [loginViaSSO, setLoginViaSSO] = useState(false);
    const [createNewOrg, setCreateNewOrg] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [phoneSubmitted, setPhoneSubmitted] = useState(false);

    // Phone is optional in appstore registration; an invalid (non-empty) number blocks submit (create flow).
    const phoneField = usePhoneField("", { submitted: phoneSubmitted });

    const shopUrl = `https://${queryParameters.get("shop")}/admin`;
    const authDataExisted = queryParameters.get("authDataExisted");

    const { progress, setProgress, statusText } = useRegistrationLoadingBar(
        appstoreSetup.shopifyAppstoreNewOrgCreationLoading,
        authDataExisted
    );

    const { isProduction, analytics } = useAnalytics();

    // The below objects map to SetupRegistrationRequestDto and should not be altered

    const [newOrgAndUser, setNewOrgAndUser] = useState({
        organizationName: queryParameters.get("shop"),
        email: "",
        firstName: "",
        lastName: "",
        acceptTos: false,
        acceptDpa: false,
        password: "",
        passwordRepeat: "",
        connectedAuthDataExternalUUID: queryParameters.get("connectedAuthDataExternalUUID"),
        shopifyAppCustomer: true,
    });

    const [existingUser, setExistingUser] = useState({
        email: "",
        password: "",
        submitted: false,
        organizationExternalUUID: "",
        selectedOrganizationExternalUUID: "",
        connectedAuthDataExternalUUID: queryParameters.get("connectedAuthDataExternalUUID"),
        organizationName: queryParameters.get("shop"),
        shopifyAppCustomer: true,
        emailVerificationCode: null,
    });

    useEffect(() => {
        if (localStorage.getItem("user")) {
            dispatch(logout());
        }

        if (queryParameters.get("oneTimeToken")) {
            dispatch(autoLoginShopifyWithOneTimeToken(queryParameters.get("oneTimeToken")));
        } else if (authDataExisted === "false") {
            setRegistrationPrompt(
                'Willkommen bei pathway. Wenn du bereits ein Konto bei uns hast, kannst du dich unter "Login" anmelden, um deinen neuen Shop hinzuzufügen.'
            );
            setShowLogin(false);
        } else {
            setRegistrationPrompt(" ");
            setShowLogin(true);
        }
    }, [dispatch, navigate, queryParameters, authDataExisted]);

    const toggleForgetPasswordModal = () => {
        setForgetPasswordModal(!forgetPasswordModal);
        setResetSuccessMessage("");
        setResetMail("");
    };

    const handleChange = (e) => {
        const { name } = e.target;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        if (name === "email") {
            setEmailError("");
        }
        setNewOrgAndUser({
            ...newOrgAndUser,
            [name]: value,
        });
    };

    const handleEmailBlur = useCallback((value) => {
        if (value) {
            const trimmed = value.trim();
            if (trimmed !== value) {
                setNewOrgAndUser((prev) => ({ ...prev, email: trimmed }));
            }
            if (trimmed.includes(" ")) {
                setEmailError("E-Mail-Adresse enthält ungültige Leerzeichen");
            } else if (trimmed && !isEmail(trimmed)) {
                setEmailError("Ungültige E-Mail-Adresse");
            } else {
                setEmailError("");
            }
            return trimmed;
        }
        return value || "";
    }, []);


    const handleSubmitRegistration = async (
        e,
        registrationData,
        submitAction,
        { collectsPhoneNumber = false, skipPasswordLogin = false } = {}
    ) => {
        e.preventDefault();
        dispatch(appstoreSetupReducerActions.clearErrorMessages());

        const trimmedEmail = registrationData.email?.trim();
        if (!trimmedEmail) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError("E-Mail-Adresse ist erforderlich"));
        }
        if (trimmedEmail.includes(" ")) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError("E-Mail-Adresse enthält ungültige Leerzeichen"));
        }
        if (!isEmail(trimmedEmail)) {
            return dispatch(appstoreSetupReducerActions.setRegistrationError("Ungültige E-Mail-Adresse"));
        }

        if (collectsPhoneNumber) {
            setPhoneSubmitted(true);
            if (!phoneField.validForSubmit) {
                return dispatch(appstoreSetupReducerActions.setRegistrationError(phoneField.errorMessage));
            }
        }

        const trimmedRegistrationData = {
            ...registrationData,
            email: trimmedEmail,
            phoneNumber: collectsPhoneNumber ? phoneField.value || undefined : undefined,
        };

        if (
            trimmedRegistrationData.passwordRepeat &&
            trimmedRegistrationData.password &&
            trimmedRegistrationData.password !== trimmedRegistrationData.passwordRepeat
        ) {
            return dispatch(appstoreSetupReducerActions.setPasswordMismatchError());
        }
        try {
            const result = await dispatch(submitAction(trimmedRegistrationData));
            setProgress(100);
            if (isProduction) {
                submitAnalytics(result.externalUUID, analytics);
            }

            if (skipPasswordLogin) {
                // SSO sessions are already authenticated and the org was selected by
                // the submitAction; there is no password to re-login with, so just
                // finish the flow.
                dispatch(appstoreSetupReducerActions.closeCreationModal());
                // replace so the back button doesn't return to this onboarding screen
                navigate("/", { replace: true });
            } else {
                try {
                    await dispatch(login(trimmedRegistrationData.email, trimmedRegistrationData.password, result.externalUUID, "/"));
                    dispatch(appstoreSetupReducerActions.closeCreationModal());
                } catch (loginError) {
                    dispatch(appstoreSetupReducerActions.setRegistrationError(loginError));
                }
            }
        } catch (submitError) {
            dispatch(appstoreSetupReducerActions.setRegistrationError(submitError));
        }
    };
    const handleSubmitNewOrgRegistration = (e) => {
        handleSubmitRegistration(e, newOrgAndUser, submitNewOrganizationAndUserRegistrationIncludingExistingAuthData, {
            collectsPhoneNumber: true,
        });
    };

    const handleSubmitNewUserRegistration = (e) => {
        // add new user to already known org
        handleSubmitRegistration(e, newOrgAndUser, submitNewUserToExistingShopifyOrg, { collectsPhoneNumber: true });
    };

    const handleResetMailChange = (e) => {
        e.preventDefault();
        const value = e.target.value;
        setResetMail(value);
    };

    const handleExistingUserLoginChange = (e) => {
        e.preventDefault();
        const { name } = e.target;
        const value = e.target.value;
        setExistingUser({
            ...existingUser,
            organizationExternalUUID: queryParameters.get("organizationExternalUUID"),
            [name]: value,
        });
    };

    const submitExistingUserInitialLogin = async () => {
        dispatch(appstoreSetupReducerActions.clearErrorMessages());
        setLoginViaSSO(false);
        try {
            await dispatch(connectNewShopToExistingUserInitialLogin(existingUser));
            setLoginSubmitted(true);
        } catch (error) {
            // error surfaced via appstoreSetup.existingUserLoginError; stay on login step
        }
    };

    // Carries the authenticated SSO identity into phase 2 (org selection + create-shop).
    // handleSubmitRegistration's validation needs an email, but SSO users never typed
    // one, so it's copied over from the authenticated identity.
    const advanceToOrgSelectionAfterSSO = (user) => {
        setExistingUser((prev) => ({
            ...prev,
            email: user?.mail || user?.email || prev.email,
            organizationExternalUUID: queryParameters.get("organizationExternalUUID"),
        }));
        setLoginViaSSO(true);
        setLoginSubmitted(true);
    };

    const submitExistingUserInitialLoginViaSSO = async (providerId) => {
        try {
            dispatch(appstoreSetupReducerActions.clearErrorMessages());
            const user = await dispatch(connectNewShopToExistingUserInitialLoginViaSSO(providerId));
            // null => the account has MFA; stay on the login step so the TOTP-code
            // input (now shown via existingUserMfaRequired) can complete the sign-in.
            if (!user) {
                return;
            }
            advanceToOrgSelectionAfterSSO(user);
        } catch (error) {
            // error surfaced via appstoreSetup.existingUserLoginError; stay on login step
        }
    };

    const cancelExistingUserLoginMfa = () => {
        dispatch(cancelConnectNewShopToExistingUserSSOMfa());
        setExistingUser((prev) => ({ ...prev, mfaCode: "" }));
    };

    const submitExistingUserLoginMfa = async () => {
        try {
            dispatch(appstoreSetupReducerActions.clearErrorMessages());
            const user = await dispatch(resolveConnectNewShopToExistingUserSSOMfa(existingUser.mfaCode));
            // null => invalid/expired code; stay on the code step (error shown inline).
            if (!user) {
                return;
            }
            advanceToOrgSelectionAfterSSO(user);
        } catch (error) {
            // error surfaced via appstoreSetup.existingUserLoginError; stay on code step
        }
    };

    // authDataExisted=true: the shop is already bound to organizationExternalUUID, so
    // existing-user login just authenticates then attaches the user to the nounce (no
    // org selection / create-shop step). Email/password and SSO share the same finish.
    const existingStoreLoginData = () => ({
        ...existingUser,
        organizationExternalUUID: queryParameters.get("organizationExternalUUID"),
        connectedAuthDataExternalUUID: queryParameters.get("connectedAuthDataExternalUUID"),
        organizationName: queryParameters.get("shop"),
    });

    const finishExistingStoreLogin = () => {
        dispatch(appstoreSetupReducerActions.closeCreationModal());
        // replace so the back button doesn't return to this onboarding screen
        navigate("/", { replace: true });
    };

    const submitExistingStoreLogin = async () => {
        try {
            dispatch(appstoreSetupReducerActions.clearErrorMessages());
            const user = await dispatch(logExistingUserIntoExistingStore(existingStoreLoginData()));
            // null => the account has MFA; stay on the login step for the TOTP-code input.
            if (!user) {
                return;
            }
            finishExistingStoreLogin();
        } catch (error) {
            // error surfaced via appstoreSetup.existingUserLoginError; stay on login step
        }
    };

    const submitExistingStoreLoginViaSSO = async (providerId) => {
        try {
            dispatch(appstoreSetupReducerActions.clearErrorMessages());
            const user = await dispatch(logExistingUserIntoExistingStoreViaSSO(providerId, existingStoreLoginData()));
            if (!user) {
                return;
            }
            finishExistingStoreLogin();
        } catch (error) {
            // error surfaced via appstoreSetup.existingUserLoginError; stay on login step
        }
    };

    const submitExistingStoreLoginMfa = async () => {
        try {
            dispatch(appstoreSetupReducerActions.clearErrorMessages());
            const user = await dispatch(
                resolveLogExistingUserIntoExistingStoreMfa(existingUser.mfaCode, existingStoreLoginData())
            );
            // null => invalid/expired code; stay on the code step (error shown inline).
            if (!user) {
                return;
            }
            finishExistingStoreLogin();
        } catch (error) {
            // error surfaced via appstoreSetup.existingUserLoginError; stay on code step
        }
    };

    const submitSelectedOrganizationAndClient = async (e) => {
        try {
            if (!createNewOrg) {
                // add new shop for existing org connected to user
                await handleSubmitRegistration(e, existingUser, connectNewShopToExistingOrgAndUser, {
                    skipPasswordLogin: loginViaSSO,
                });
            } else {
                // create new org for existing user
                await handleSubmitRegistration(e, existingUser, createNewOrgForExistingUser, {
                    skipPasswordLogin: loginViaSSO,
                });
            }
        } catch (error) {
            //     error
        }
    };

    const handleSelectOrgAndClientChange = (e) => {
        e.preventDefault();
        const { name } = e.target;
        const value = e.target.value;

        if (name === "selectedOrganizationExternalUUID") {
            if (value === "createNewOrganization") {
                setCreateNewOrg(true);
            } else setCreateNewOrg(false);
        }
        setExistingUser({
            ...existingUser,
            [name]: value,
        });
    };

    const resetPassword = async () => {
        try {
            dispatch(setUpResetPassword(resetMail));
            setResetSuccessMessage(
                "Falls die E-Mail in unserer Datenbank vorhanden ist, werden wir einen Link zum Zurücksetzen an die von dir angegebene E-Mail schicken."
            );
            setResetMail(null);
        } catch (error) {
            // error
        }
    };

    return (
        <SplitScreenContainer>
            <ModalInfo
                isOpen={appstoreSetup.shopifyAppstoreNewOrgModalOpen}
                size={"lg"}
                toggleModal={() => dispatch(appstoreSetupReducerActions.closeCreationModal())}
                disabled={appstoreSetup.shopifyAppstoreNewOrgCreationLoading}
                primaryButtonText="Zurück"
                title={"Fast geschafft..."}
            >
                <>
                    {(authentication.errorMessage || appstoreSetup.shopifyNewUserError) && (
                        <AlertComponent errorMessages={appstoreSetup.shopifyNewUserError} />
                    )}
                    {appstoreSetup.shopifyAppstoreNewOrgCreationLoading && (
                        <Progress
                            className="my-2"
                            value={progress}
                            style={{
                                height: "20px",
                            }}
                        >
                            {statusText}
                        </Progress>
                    )}
                </>
            </ModalInfo>
            <Modal isOpen={forgetPasswordModal} toggle={toggleForgetPasswordModal} centered={true}>
                <ModalHeader>Passwort vergessen</ModalHeader>
                <ModalBody>
                    <PathwayFormGrid>
                        <PathwayTextInputGroup
                            label={"E-Mail"}
                            name={"resetMail"}
                            placeHolder={"E-Mail"}
                            handleChange={handleResetMailChange}
                            value={resetMail}
                            mandatory
                        />
                        <AlertComponent
                            errorMessages={authentication.resetErrorMessage}
                            successMessages={resetSuccessMessage}
                        />
                    </PathwayFormGrid>
                </ModalBody>
                <ModalFooter>
                    <LoadingSpinnerButton
                        onClick={resetPassword}
                        loading={authentication.resetLoading}
                        disabled={resetSuccessMessage || !resetMail}
                    >
                        Passwort zurücksetzen
                    </LoadingSpinnerButton>
                    <Button color="secondary" onClick={toggleForgetPasswordModal}>
                        {resetMail ? "Abbrechen" : "Schließen"}
                    </Button>
                </ModalFooter>
            </Modal>

            <LeftSection>
                <BackToShopifyLink>Zurück zu Shopify</BackToShopifyLink>
                {!showLogin ? (
                    <>
                        <HeaderText>Registrierung</HeaderText>
                        <LeftSubtitleContainer>
                            <LeftSubtitleText>Du hast schon einen Account?</LeftSubtitleText>
                            <LeftSubtitleLink onClick={() => setShowLogin(true)}>Jetzt anmelden</LeftSubtitleLink>
                        </LeftSubtitleContainer>
                        <AstronautImage
                            src="/astronaut_coffee.png"
                            alt="Astronaut with Coffee"
                            className="rounded mx-auto d-block"
                        />
                    </>
                ) : (
                    <>
                        <HeaderText>
                            Das manuelle Buchen
                            <br />
                            überspringen
                        </HeaderText>
                        <AstronautImage
                            src="/astronaut.svg"
                            alt="Astronaut"
                            className="rounded mx-auto d-block"
                            style={{ marginTop: "50px" }}
                        />
                    </>
                )}
            </LeftSection>

            <RightSection>
                <RegistrationFormContainer>
                    <LogoContainer>
                        <img
                            src="/logo_black.png"
                            alt="Pathway Logo"
                            style={{ maxWidth: "200px", marginBottom: "2rem" }}
                            className="rounded mx-auto d-block"
                        />
                    </LogoContainer>

                    {!showLogin ? (
                        <PageTitle>Erstelle ein neues Konto</PageTitle>
                    ) : (
                        <SubtitleText>
                            {" "}
                            {registrationPrompt ||
                                "Willkommen bei pathway - starte jetzt mit der Integration deines Shopify Stores"}{" "}
                        </SubtitleText>
                    )}

                    {authDataExisted === "true" && (
                        <ShopifyConnectionCallout>
                            Dieser Shop ist mit Pathway verbunden. Du kannst jetzt ein neues Konto erstellen oder Dich
                            bei einem bestehenden Konto anmelden. Bitte beachte, dass für zusätzliche Benutzer 10 € pro
                            Monat in Rechnung gestellt werden.
                        </ShopifyConnectionCallout>
                    )}

                    {(appstoreSetup.shopifyNewUserLoading || authentication.loading) && (
                        <LoadingContainer>
                            <LoadingSpinner loading={appstoreSetup.shopifyNewUserLoading || authentication.loading} />
                        </LoadingContainer>
                    )}

                    {(!appstoreSetup.shopifyNewUserLoading || !authentication.loading) && (
                        <RegistrationContent>
                            {appstoreSetup.shopifyNewUserError && (
                                <AlertComponent errorMessages={appstoreSetup.shopifyNewUserError} />
                            )}

                            {authDataExisted === "false" && (
                                <>
                                    {!showLogin ? (
                                        <>
                                            <ShopifyAppstoreNewUserRegistration
                                                shop={queryParameters.get("shop")}
                                                authDataExisted={authDataExisted}
                                                appstoreSetup={appstoreSetup}
                                                submit={handleSubmitNewOrgRegistration}
                                                newOrgAndUser={newOrgAndUser}
                                                handleChange={handleChange}
                                                handleEmailBlur={handleEmailBlur}
                                                emailError={emailError}
                                                phoneInputProps={phoneField.inputProps}
                                                shopUrl={shopUrl}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <ShopifyAppstoreExistingUserLogin
                                                queryParameters={queryParameters}
                                                handleExistingUserLoginChange={handleExistingUserLoginChange}
                                                existingUser={existingUser}
                                                appstoreSetup={appstoreSetup}
                                                submit={
                                                    loginSubmitted
                                                        ? submitSelectedOrganizationAndClient
                                                        : appstoreSetup.existingUserMfaRequired
                                                          ? submitExistingUserLoginMfa
                                                          : submitExistingUserInitialLogin
                                                }
                                                toggleForgetPasswordModal={toggleForgetPasswordModal}
                                                loginSubmitted={loginSubmitted}
                                                handleSelectOrgAndClientChange={handleSelectOrgAndClientChange}
                                                authDataType={queryParameters.get("authDataType")}
                                                needsEmailCodeVerification={
                                                    authentication?.user?.needsEmailCodeVerification
                                                }
                                                onGoogleLogin={() => submitExistingUserInitialLoginViaSSO("google")}
                                                onMicrosoftLogin={() =>
                                                    submitExistingUserInitialLoginViaSSO("microsoft")
                                                }
                                                onCancelMfa={cancelExistingUserLoginMfa}
                                            />
                                            <BackToRegistrationContainer>
                                                <BackToRegistrationText>Noch kein Account?</BackToRegistrationText>
                                                <PathwayLinkButton onClick={() => setShowLogin(false)}>
                                                    Jetzt registrieren
                                                </PathwayLinkButton>
                                            </BackToRegistrationContainer>
                                        </>
                                    )}
                                </>
                            )}

                            {(!appstoreSetup.shopifyNewUserLoading || !authentication.loading) &&
                                queryParameters.get("organizationExternalUUID") &&
                                authDataExisted === "true" && (
                                    <>
                                        {!showLogin ? (
                                            <>
                                                <ShopifyAppstoreNewUserRegistration
                                                    shop={queryParameters.get("shop")}
                                                    authDataExisted={authDataExisted}
                                                    appstoreSetup={appstoreSetup}
                                                    submit={handleSubmitNewUserRegistration}
                                                    newOrgAndUser={newOrgAndUser}
                                                    handleChange={handleChange}
                                                    handleEmailBlur={handleEmailBlur}
                                                    emailError={emailError}
                                                    phoneInputProps={phoneField.inputProps}
                                                    shopUrl={shopUrl}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <ShopifyAppstoreExistingUserLogin
                                                    queryParameters={queryParameters}
                                                    handleExistingUserLoginChange={handleExistingUserLoginChange}
                                                    existingUser={existingUser}
                                                    appstoreSetup={appstoreSetup}
                                                    submit={
                                                        appstoreSetup.existingUserMfaRequired
                                                            ? submitExistingStoreLoginMfa
                                                            : submitExistingStoreLogin
                                                    }
                                                    toggleForgetPasswordModal={toggleForgetPasswordModal}
                                                    loginSubmitted={false}
                                                    handleSelectOrgAndClientChange={handleSelectOrgAndClientChange}
                                                    authDataType={queryParameters.get("authDataType")}
                                                    onGoogleLogin={() => submitExistingStoreLoginViaSSO("google")}
                                                    onMicrosoftLogin={() => submitExistingStoreLoginViaSSO("microsoft")}
                                                    onCancelMfa={cancelExistingUserLoginMfa}
                                                />
                                                <BackToRegistrationContainer>
                                                    <BackToRegistrationText>Noch kein Account?</BackToRegistrationText>
                                                    <PathwayLinkButton onClick={() => setShowLogin(false)}>
                                                        Jetzt registrieren
                                                    </PathwayLinkButton>
                                                </BackToRegistrationContainer>
                                            </>
                                        )}
                                    </>
                                )}
                        </RegistrationContent>
                    )}
                </RegistrationFormContainer>
            </RightSection>
        </SplitScreenContainer>
    );
};

export default ShopifyRegistrationScreen;

const SplitScreenContainer = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;

    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
        min-height: 100vh;
    }
`;

const LeftSection = styled.div`
    flex: 1;
    background: #effbff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;

    @media (max-width: 768px) {
        flex: none;
        padding: 1.5rem 1rem;
        min-height: 40vh;
        justify-content: flex-start;
        order: 2; /* Move to bottom on mobile */
    }
`;

const HeaderText = styled.h2`
    font-family: "Inter", sans-serif;
    font-size: 28px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 22px;
        margin-bottom: 1rem;
    }
`;

const LeftSubtitleText = styled.p`
    font-family: "Inter", sans-serif;
    font-size: 16px;
    text-align: center;
    color: #1f2937;
    margin: 0;
    line-height: 1.5;

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const LeftSubtitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 30px;
    max-width: 400px;
    justify-content: center;

    @media (max-width: 768px) {
        margin-bottom: 1.5rem;
        flex-direction: column;
        text-align: center;
        gap: 0.25rem;
    }
`;

const LeftSubtitleLink = styled.button`
    background: none;
    border: none;
    font-family: "Inter", sans-serif;
    font-size: 16px;
    color: #6c757d;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    transition: color 0.2s ease;

    &:hover {
        color: #495057;
    }

    &:focus {
        outline: none;
        color: #495057;
    }

    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const RightSection = styled.div`
    flex: 1;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    overflow-y: auto;

    @media (max-width: 768px) {
        flex: none;
        padding: 1rem;
        align-items: flex-start;
        min-height: 60vh;
        order: 1; /* Move to top on mobile */
    }
`;

const RegistrationFormContainer = styled.div`
    width: 100%;
    max-width: 500px;
    margin-top: 50px;

    @media (max-width: 768px) {
        max-width: 100%;
        margin-top: 1rem;
        padding: 0;
    }
`;

const PageTitle = styled.h1`
    font-family: "Inter", sans-serif;
    font-size: 24px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    margin-bottom: 1rem;
    margin-top: 50px;

    @media (max-width: 768px) {
        font-size: 20px;
        margin-top: 1rem;
        margin-bottom: 0.75rem;
    }
`;

const SubtitleText = styled.p`
    font-family: "Inter", sans-serif;
    font-size: 14px;
    text-align: center;
    color: #6b7280;
    margin-bottom: 2rem;
    line-height: 1.5;

    @media (max-width: 768px) {
        font-size: 13px;
        margin-bottom: 1.5rem;
    }
`;

const RegistrationContent = styled.div`
    display: grid;
    row-gap: 1.5rem;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        row-gap: 1rem;
        margin-bottom: 1.5rem;
    }
`;

const LoadingContainer = styled.div`
    display: grid;
    justify-content: center;
    align-items: center;
`;

const BackToRegistrationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
`;

const BackToRegistrationText = styled.span`
    font-family: "Inter", sans-serif;
    font-size: 14px;
    color: #6b7280;

    @media (max-width: 768px) {
        font-size: 13px;
    }
`;

const ShopifyConnectionCallout = styled.div`
    background-color: #dcfce7;
    border: 1px solid #86efac;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 10px;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #166534;
    line-height: 1.4;

    @media (max-width: 768px) {
        padding: 10px 12px;
        margin-bottom: 0.75rem;
        font-size: 11px;
    }
`;

const AstronautImage = styled.img`
    max-width: 60%;
    opacity: 1;

    @media (max-width: 768px) {
        max-width: 50%;
    }
`;

const LogoContainer = styled.div`
    padding: 10px;
    border-radius: 5px;
    text-align: center;
    position: relative;
    margin-top: 2.5rem;

    @media (max-width: 768px) {
        margin-top: 0;
        padding: 5px;

        img {
            max-width: 150px !important;
            margin-bottom: 1rem !important;
        }
    }
`;

const BackToShopifyLink = styled.div`
    font-family: "Inter", sans-serif;
    font-size: 14px;
    color: #6c757d;
    cursor: pointer;
    text-decoration: underline;
    position: absolute;
    top: 2rem;
    left: 2rem;
    transition: color 0.2s ease;

    &:hover {
        color: #495057;
    }

    @media (max-width: 768px) {
        position: relative;
        top: auto;
        left: auto;
        margin-bottom: 1rem;
        font-size: 13px;
    }
`;
